import { Box, Typography } from "@outschool/backpack";
import { Activity } from "@outschool/gql-backend-generated";
import { Trans, useTranslation } from "@outschool/localization";
import React from "react";

import { useMobileCDPGroupExperiment } from "../hooks/experiments/useMobileCDPGroupExperiment";
import usePriceDetailsString from "../hooks/usePriceDetailsString";

export default function ActivityPrice({
  activity,
}: {
  activity: Pick<
    Activity,
    | "allows_recurring_payment"
    | "isClub"
    | "is_ongoing_weekly"
    | "duration_weeks"
    | "hasTeacherSchedule"
    | "price_cents"
    | "weekly_meetings"
    | "isFlexSchedule"
    | "size_max"
    | "isOneTime"
    | "isSelfPaced"
  >;
}) {
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\MobileActivityPrice"
  );
  const { totalPriceString, weeklyPriceString, perMeetingPriceString } =
    usePriceDetailsString();
  const enableMobileCDPGroup1Exp = useMobileCDPGroupExperiment();

  const flexSx = { flexDirection: "row", gap: 4, alignItems: "end" };
  let pricePrefix = "";

  if (enableMobileCDPGroup1Exp) {
    flexSx.alignItems = "center;";
    pricePrefix = "charged ";
  }

  if (activity.hasTeacherSchedule) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Typography variant="h3">
            {{ perMeetingPrice: perMeetingPriceString(activity) }}
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {" "}
            {pricePrefix}per session
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.isOneTime) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Typography variant="h3">
            {{ perMeetingPrice: perMeetingPriceString(activity) }}
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {" "}
            {pricePrefix}per class
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.isClub) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Typography variant="h3">
            {{ perMeetingPrice: perMeetingPriceString(activity) }}
          </Typography>

          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {" "}
            {pricePrefix}monthly
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.is_ongoing_weekly && activity.weekly_meetings === 1) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Typography variant="h3">
            {{ weeklyPrice: weeklyPriceString(activity) }}
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {" "}
            {pricePrefix}weekly
          </Typography>
        </Box>
      </Trans>
    );
  } else if (
    activity.is_ongoing_weekly &&
    (activity.weekly_meetings || 0) > 1
  ) {
    return (
      <Trans t={t}>
        <Box flex sx={{ flexDirection: "column" }}>
          <Box flex sx={flexSx}>
            <Typography variant="h3">
              {{ weeklyPrice: weeklyPriceString(activity) }}
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              {" "}
              {pricePrefix}per week
            </Typography>
          </Box>
          <Typography variant="body2">
            ({{ perMeetingPrice: perMeetingPriceString(activity) }}{" "}
            {pricePrefix}per class)
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.isSelfPaced) {
    if (activity.duration_weeks === 1) {
      return (
        <Trans t={t}>
          <Box flex sx={{ flexDirection: "column" }}>
            <Box flex sx={flexSx}>
              <Typography variant="h3">
                {{ totalPrice: totalPriceString(activity) }}
              </Typography>

              <Typography variant="body2" sx={{ marginBottom: 1 }}>
                {" "}
                {pricePrefix}for all content
              </Typography>
            </Box>
          </Box>
        </Trans>
      );
    }
    return (
      <Trans t={t}>
        <Box flex sx={{ flexDirection: "column" }}>
          <Box flex sx={flexSx}>
            <Typography variant="h3">
              {{ weeklyPrice: weeklyPriceString(activity) }}
            </Typography>

            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              {" "}
              {pricePrefix}weekly
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2">or </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {{ totalPrice: totalPriceString(activity) }}
            </Typography>
            <Typography variant="body2">
              {" "}
              {pricePrefix}for all content
            </Typography>
          </Box>
        </Box>
      </Trans>
    );
  } else if (!activity.allows_recurring_payment) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Typography variant="h3">
            {{ totalPrice: totalPriceString(activity) }}
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {" "}
            {pricePrefix}for{" "}
            {{
              classes:
                (activity.duration_weeks || 0) *
                (activity.weekly_meetings || 0),
            }}{" "}
            classes
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.allows_recurring_payment) {
    return (
      <Trans t={t}>
        <Box flex sx={{ flexDirection: "column" }}>
          <Box flex sx={flexSx}>
            <Typography variant="h3">
              {{ weeklyPrice: weeklyPriceString(activity) }}
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              {" "}
              {pricePrefix}weekly
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2">or </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {{ totalPrice: totalPriceString(activity) }}
            </Typography>
            <Typography variant="body2">
              {" "}
              {pricePrefix}for{" "}
              {{
                classes:
                  (activity.duration_weeks || 0) *
                  (activity.weekly_meetings || 0),
              }}{" "}
              classes
            </Typography>
          </Box>
        </Box>
      </Trans>
    );
  } else {
    return null;
  }
}
